import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
// Firebase
import { projectFirestore } from "../../firebase/config";
// Components
import Error from "../../components/Error";
import Loading from "../../components/Loading";
import ProjectForm from "./components/ProjectForm";
// Styles
import "../../styles/pages/ProjectEditor.css";

const EditProject = () => {
  // Project and request state
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Get the project ID from the URL
  const { id } = useParams();
  // Used to navigate after the project has been updated
  const navigate = useNavigate();

  // Fetch the existing project when the page loads
  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true);
      setError(null);

      try {
        const docRef = doc(projectFirestore, "projects", id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          setError("Project not found.");
          return;
        }

        setProjectData({
          id: docSnap.id,
          ...docSnap.data(),
        });
      } catch (err) {
        setError("Failed to load project.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  // Update the project in Firestore
  const handleSubmit = async (formData) => {
    if (saving) return;

    setSaving(true);
    setError(null);

    try {
      const docRef = doc(projectFirestore, "projects", id);
      await updateDoc(docRef, {
        ...formData,
        lastModified: serverTimestamp(),
      });

      // Return to the project after the update succeeds
      navigate(`/projects/${id}`, { replace: true });
    } catch (err) {
      setError("Failed to save project.");
      // Re-throw so ProjectForm knows the submission did not succeed
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // Show the shared loading state while the project is fetched
  if (loading) return <Loading message="Loading project" />;
  // Show the shared error state if the project cannot be loaded
  if (error && !projectData) return <Error message={error} />;

  return (
    <main className="project-editor-page position-relative overflow-hidden">
      {/* Subtle background grid */}
      <div
        className="project-editor-grid position-absolute top-0 start-0 w-100 h-100"
        aria-hidden="true"
      />
      {/* Decorative background text */}
      <div
        className="project-editor-background-text position-absolute user-select-none"
        aria-hidden="true"
      >
        EDIT
      </div>
      <div className="container position-relative z-1">
        {/* Back to project */}
        <Link
          to={`/projects/${id}`}
          className="project-editor-back d-inline-flex align-items-center gap-2 text-decoration-none mb-4"
        >
          <i className="bi bi-arrow-left" aria-hidden="true" />
          Back to project
        </Link>
        {/* Page header */}
        <div className="project-editor-header mb-5">
          <span className="project-editor-eyebrow font-monospace text-danger d-block mb-3">
            PROJECT / EDIT
          </span>
          <h1 className="project-editor-title fw-bold mb-3">Edit project.</h1>
          <p className="project-editor-description text-secondary mb-0">
            Update the project details, contributors, tags and external links.
          </p>
        </div>
        {/* Project form */}
        <ProjectForm
          initialData={projectData}
          onSubmit={handleSubmit}
          isPending={saving}
          error={error}
          submitLabel="Save Changes"
        />
      </div>
    </main>
  );
};

export default EditProject;
